/**
 * A stay's care plan — the `care` category of the planning service's plan
 * resource.
 *
 * WHAT CHANGED, AND WHY THE BOARD STILL LOOKS THE SAME. A care plan used to be
 * its own resource under the patient, with four command verbs and a fold this
 * service computed. It is now one plan among others: the planning service owns
 * every plan, the board reads `GET /plans/care?subject=stay/{id}`
 * through the gateway, and one `PUT /plans/care/{id}` replaces the item list
 * against the version it was read at. The four verbs are gone as WIRE calls;
 * what a nurse means by them is still four intents, applied to the item list
 * here and sent as one write (see `applyIntent`).
 *
 * A REPLACE IS NOT A PUBLISH. The published version is the one in force, and a
 * replace after a publish is a new proposal. A board that edits and does not
 * publish has changed nothing the ward acts on, which is why the board's save
 * path publishes as well — see useCarePlan.
 *
 * THE ITEM VOCABULARY IS THE CATEGORY'S. `CarePlanItem` below mirrors the
 * `care` item schema the planner declares. It is narrowed from the generic
 * `PlanItem` rather than replacing it: everything generic about a plan stays in
 * endpoints/plans.ts and nothing there knows a weekday from a discipline.
 */

import type { Plan, PlanItem } from './plans';

/**
 * The days a care plan can be written on.
 *
 * Seven, not five. The category's item schema closes `day` at seven values and
 * says why: which days a ward plans on is the ward's choice, and a plan written
 * on a Saturday must still be storable. A board that drew Monday to Friday
 * would render six days of a seven-day plan and show nothing for the seventh.
 */
export const CARE_PLAN_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export type CarePlanDay = (typeof CARE_PLAN_DAYS)[number];

/**
 * One planned act on a stay: the `care` category's item.
 *
 * `id` is a UUID the client mints, and is how every later edit names this item.
 * It used to be a free-form string; every category's item id is a UUID now,
 * because matching an item across two writes is one rule for every category.
 *
 * `assignee_id` and `assignee_name` travel together or not at all — the
 * category's schema requires each when the other is present. An unassigned act
 * carries NEITHER, where the old resource carried two empty strings: absent is
 * how the schema spells "nobody yet", and an empty string would be a person
 * whose name is blank.
 *
 * `assignee_name` is a SNAPSHOT taken when the assignment was made, not a join
 * against the person's current record. The board renders it as given; looking
 * the name up again would rewrite what the plan said on the day it was written.
 */
export interface CarePlanItem extends PlanItem {
  id: string;
  title: string;
  discipline_id: string;
  day: CarePlanDay;
  /** HH:MM on a 24-hour clock, the same string the board shows in the cell. */
  time: string;
  duration_minutes: number;
  assignee_id?: string;
  assignee_name?: string;
}

/** A plan of the `care` category, with its items narrowed. */
export interface CarePlan extends Plan {
  items: CarePlanItem[];
}

/** The `care` category's name, as the planner declares it. */
export const CARE_CATEGORY = 'care';

/** The subject type a care plan names. */
export const CARE_SUBJECT_TYPE = 'stay';

/**
 * What a nurse meant, in terms the board can re-apply against a plan that moved
 * underneath her.
 *
 * An intent carries no version. That is what makes the 412 path possible: on a
 * refusal the board re-reads the plan and re-applies the same intent to the
 * item list it just read, instead of asking her to retype what she already
 * said.
 */
export type CarePlanIntent =
  | { verb: 'add-intervention'; item: CarePlanItem }
  | { verb: 'remove-intervention'; itemId: string }
  | {
      verb: 'retime';
      itemId: string;
      day: CarePlanDay;
      time: string;
      duration_minutes: number;
    }
  | {
      verb: 'assign';
      itemId: string;
      assignee_id: string;
      assignee_name: string;
    };

/** The item an intent names, or null for an add, which names an id nothing holds yet. */
export function intentTarget(intent: CarePlanIntent): string | null {
  return intent.verb === 'add-intervention' ? null : intent.itemId;
}

/**
 * Apply one intent to an item list, returning the list to send.
 *
 * Pure, and deliberately so: the same function produces the write and the
 * re-application after a 412, so the second attempt cannot mean something
 * different from the first. It never mutates the list it was given, because
 * that list is what react-query holds for every other reader of this plan.
 *
 * An intent naming an item the plan does not hold returns null. That is the one
 * conflict a board cannot resolve on a nurse's behalf: re-applying a retime to
 * an item somebody removed would either recreate it under its old id or do
 * nothing quietly, and both are worse than telling her what changed.
 */
export function applyIntent(items: CarePlanItem[], intent: CarePlanIntent): CarePlanItem[] | null {
  if (intent.verb === 'add-intervention') {
    return [...items, intent.item];
  }
  const index = items.findIndex((item) => item.id === intent.itemId);
  if (index === -1) return null;

  if (intent.verb === 'remove-intervention') {
    return items.filter((item) => item.id !== intent.itemId);
  }

  const next = [...items];
  const current = next[index];
  if (intent.verb === 'retime') {
    next[index] = {
      ...current,
      day: intent.day,
      time: intent.time,
      duration_minutes: intent.duration_minutes,
    };
    return next;
  }

  // Assign, or unassign. Unassigning DROPS both fields rather than blanking
  // them: the category's schema requires each when the other is present, so an
  // empty pair is not a value it accepts.
  if (intent.assignee_id === '' && intent.assignee_name === '') {
    const { assignee_id: _id, assignee_name: _name, ...unassigned } = current;
    next[index] = unassigned as CarePlanItem;
    return next;
  }
  next[index] = {
    ...current,
    assignee_id: intent.assignee_id,
    assignee_name: intent.assignee_name,
  };
  return next;
}
